/**
 * Site setup — orchestrator that wires framework, commerce, and sections.
 *
 * Actual logic lives in focused modules:
 *   setup/commerce-loaders.ts  — COMMERCE_LOADERS map (VTEX + site data fetchers)
 *   setup/section-loaders.ts   — registerSectionLoaders (per-section prop enrichment)
 *
 * Section metadata (eager, sync, layout, cache, LoadingFallback) is declared
 * in each section file and auto-extracted by the generate CLI.
 */

import "./cache-config";

import {
  registerCommerceLoaders,
  registerSectionsSync,
  applySectionConventions,
  registerSeoSections,
} from "@decocms/blocks/cms";
import { autoconfigApps } from "@decocms/blocks-admin/apps/autoconfig";
import { createSiteSetup } from "@decocms/blocks/setup";
import { createAdminSetup } from "@decocms/blocks-admin/setup";
import { setInvokeLoaders } from "@decocms/blocks-admin";
import { VTEX_REGISTRY_ENTRY } from "@decocms/apps-vtex/registry";
import { createVtexFetch, initVtexFromBlocks, setVtexFetch } from "@decocms/apps-vtex";
import { createCheckoutAwareFetch } from "./server/checkoutFetch";
import { BLOG_REGISTRY_ENTRY } from "@decocms/apps-blog/registry";
import BlogPostTemplate from "./sections/Blog/TemplateAdapter";
import { blocks as generatedBlocks } from "../.deco/blocks.gen";
import { sectionMeta, syncComponents, loadingFallbacks } from "../.deco/sections.gen";
import { PreviewProviders } from "@decocms/tanstack";
// @ts-ignore Vite ?url import
import appCss from "./styles/app.css?url";

import { COMMERCE_LOADERS } from "./setup/commerce-loaders";
import "./setup/section-loaders";

// -- Framework setup (framework-generic options only) --
createSiteSetup({
  sections: import.meta.glob("./sections/**/*.tsx") as Record<string, () => Promise<any>>,
  blocks: generatedBlocks,
  productionOrigins: [
    "https://www.dt.com.br",
    "https://dt.com.br",
  ],
  initPlatform: (blocks) => initVtexFromBlocks(blocks),
  onResolveError: (error, resolveType, context) => {
    console.error(`[CMS-DEBUG] ${context} "${resolveType}" failed:`, error);
  },
  onDanglingReference: (resolveType) => {
    console.warn(`[CMS-DEBUG] Dangling reference: ${resolveType}`);
    return null;
  },
});

// -- Admin setup (admin-only options: meta schema, render shell, preview) --
createAdminSetup({
  meta: () => import("../.deco/meta.gen.json").then((m) => m.default),
  css: appCss,
  fonts: ["/fonts/D-DIN-PRO-400-Regular.otf", "/fonts/RobotoCondensed-Bold.ttf"],
  previewWrapper: PreviewProviders,
});

// Wire installed deco apps (deco-vtex) — blockKey-driven for Tanstack. Each
// platform package exports its own single-entry registry (the old combined
// @decocms/apps/registry array doesn't exist post package-split); sites compose
// the array from only the platform entries they actually use.
autoconfigApps(generatedBlocks, [VTEX_REGISTRY_ENTRY, BLOG_REGISTRY_ENTRY]);

// `autoconfigApps` is server-only (`if (typeof document !== "undefined") return`
// in @decocms/blocks-admin/apps/autoconfig), so the blog app's own
// `registerSections()` call — which feeds `blog/sections/Template` into the
// lazy/async section registry — never runs in the browser. A page section
// resolved eagerly (not deferred) with no sync-registered component renders
// SSR HTML that then blanks on hydration (see the exact warning in
// @decocms/tanstack's DecoPageRenderer: "Eager section ... is not in
// registerSectionsSync() ... may cause blank content during hydration").
// Registering it here — statically imported, so it's bundled on both server
// and client — fixes that for every page that uses this section.
registerSectionsSync({ "blog/sections/Template": BlogPostTemplate });

// -- VTEX wiring --
// `createVtexFetch()` returns an InstrumentedFetch pre-wired with traceparent
// injection, the VTEX URL → operation router (semantic span names), and the
// `commerce_request_duration_ms` histogram. It also handles `vtex_segment`
// cookie propagation for regional sellers.
// A camada resilience (timeout de 8s + circuit breaker) fica fora do checkout:
// abortar a autorização do cartão gerava pedido pago em dobro. Ver
// src/server/checkoutFetch.ts.
setVtexFetch(createVtexFetch({ baseFetch: createCheckoutAwareFetch(), resilience: false }));

// -- Convention-driven section registration --
applySectionConventions({
  meta: sectionMeta,
  syncComponents,
  loadingFallbacks,
  sectionGlob: import.meta.glob("./sections/**/*.tsx") as Record<string, () => Promise<any>>,
});

// -- Commerce + invoke --
registerCommerceLoaders(COMMERCE_LOADERS);
setInvokeLoaders(() => COMMERCE_LOADERS);

// -- SEO: PDP structured data + meta. The CMS block referenced
// "commerce/sections/Seo/SeoPDP.tsx", a v1 resolveType the v2 framework
// hardcodes into SKIP_RESOLVE_TYPES (never rendered) — repointed the
// block to this site-local implementation instead (site/sections/...).
registerSeoSections([
  "site/sections/Seo/SeoPDP.tsx",
  "site/sections/Seo/SeoPLP.tsx",
  "site/sections/Seo/SeoBlogListing.tsx",
]);
